/**
 * Trending video-ad library — flat catalogue of video format examples grouped
 * into chip-filterable categories. Thumbnails reuse the per-type preview
 * stills under `/public/ad-video-types/`. Categories drive the chip filter and
 * grid in `VideoAdsPage`'s "Browse Formats" section.
 */
import {
  PRODUCT_VIDEO_TYPES,
  type ProductVideoTypeId,
} from "./videoAdTypes";

export interface VideoAdLibraryCategory {
  id: string;
  label: string;
  description: string;
  /** Video-type ids that fall under this chip. Each id has a 1:1 entry in
   *  `PRODUCT_VIDEO_TYPES` so the dispatch carries the matching format brief. */
  videoTypeIds: readonly ProductVideoTypeId[];
}

export const VIDEO_AD_LIBRARY: readonly VideoAdLibraryCategory[] = [
  {
    id: "ugc",
    label: "UGC & Reviews",
    description:
      "Creator-led, conversational ads with natural delivery and product hand-off.",
    videoTypeIds: ["ugc", "unboxing", "product-review"],
  },
  {
    id: "cinematic",
    label: "Cinematic & Brand",
    description:
      "High-production showcases with controlled motion and brand-grade polish.",
    videoTypeIds: ["hyper-motion", "tv-spot", "wild-card"],
  },
  {
    id: "tutorial",
    label: "Tutorial",
    description:
      "Step-by-step usage sequences that make products instantly understandable.",
    videoTypeIds: ["tutorial"],
  },
  {
    id: "try-on",
    label: "Try-On",
    description:
      "Apparel and wearables shown on real bodies with believable fit and drape.",
    videoTypeIds: ["ugc-virtual-try-on", "pro-virtual-try-on"],
  },
] as const;

export interface FlatVideoLibraryEntry {
  categoryId: string;
  categoryLabel: string;
  videoTypeId: ProductVideoTypeId;
  /** Preview thumbnail (per-type still under `/ad-video-types/`). */
  url: string;
  title: string;
}

const TYPE_BY_ID = new Map(
  PRODUCT_VIDEO_TYPES.map((t) => [t.id, t] as const),
);

/** Pre-flattened list for quick filtering — order preserved from category order. */
export const VIDEO_AD_LIBRARY_FLAT: readonly FlatVideoLibraryEntry[] =
  VIDEO_AD_LIBRARY.flatMap((cat) =>
    cat.videoTypeIds
      .map((id): FlatVideoLibraryEntry | null => {
        const type = TYPE_BY_ID.get(id);
        if (!type) return null;
        return {
          categoryId: cat.id,
          categoryLabel: cat.label,
          videoTypeId: id,
          url: type.preview,
          title: type.title,
        };
      })
      .filter((e): e is FlatVideoLibraryEntry => e !== null),
  );
